import { Box, Tab, Tabs, Typography, useTheme, Card, Chip, Badge } from "@mui/material";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import EventIcon from "@mui/icons-material/Event";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const Profile = () => {
  const theme = useTheme();
  const location = useLocation();

  const tabs = [
    { 
      label: "Basic Info", 
      path: "/profile", 
      icon: <PersonIcon />,
      color: theme.colors.primary.main,
      bgGradient: `linear-gradient(135deg, ${theme.colors.primary.lighter} 0%, ${theme.colors.primary.light} 100%)`,
      completed: true
    },
    { 
      label: "Packages", 
      path: "/profile/session-package", 
      icon: <LocalHospitalIcon />,
      color: theme.colors.success.main,
      bgGradient: `linear-gradient(135deg, ${theme.colors.success.lighter} 0%, ${theme.colors.success.light} 100%)`,
      completed: false
    },
    { 
      label: "Payments", 
      path: "/profile/payment-details", 
      icon: <PaymentIcon />,
      color: theme.colors.warning.main,
      bgGradient: `linear-gradient(135deg, ${theme.colors.warning.lighter} 0%, ${theme.colors.warning.light} 100%)`,
      completed: true
    },
    { 
      label: "Slots", 
      path: "/profile/slot-management", 
      icon: <EventIcon />,
      color: theme.colors.info.main,
      bgGradient: `linear-gradient(135deg, ${theme.colors.info.lighter} 0%, ${theme.colors.info.light} 100%)`,
      completed: false
    },
  ];

  const currentTab = tabs.findIndex(tab => 
    location.pathname === tab.path || 
    (tab.path === "/profile" && location.pathname === "/profile")
  );

  return (
    <Box>
      <Box 
        mb={4} 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
      >
        <Box>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Profile Settings
          </Typography>
          <Typography variant="body1" sx={{ color: theme.colors.alpha.black[70] }}>
            Manage your account information and preferences
          </Typography>
        </Box>
        <Chip 
          icon={<CheckCircleIcon />}
          label="50% Complete" 
          sx={{ 
            fontWeight: 600,
            fontSize: "14px",
            background: `linear-gradient(135deg, ${theme.colors.success.light}, ${theme.colors.success.main})`,
            color: "white",
            "& .MuiChip-icon": {
              color: "white"
            }
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={currentTab !== -1 ? currentTab : 0}
          variant="fullWidth"
          TabIndicatorProps={{
            style: { 
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: currentTab !== -1 ? tabs[currentTab]?.color : theme.colors.primary.main,
            }
          }}
          sx={{
            minHeight: 48,
            borderBottom: `1px solid ${theme.colors.alpha.black[10]}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              minHeight: 48,
              transition: "all 0.2s ease",
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: theme.colors.alpha.black[5],
              },
            },
          }}
        >
          {tabs.map((tab, index) => {
            const isSelected = currentTab === index;
            return (
              <Tab
                key={index}
                sx={{
                  color: tab.color,
                  "&.Mui-selected": {
                    fontWeight: 700,
                  },
                }}
                label={
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={1}
                  >
                    <Box
                      sx={{
                        fontSize: 20,
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {tab.icon}
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: "inherit",
                        fontSize: "inherit",
                      }}
                    >
                      {tab.label}
                    </Typography>
                    {tab.completed && (
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 16,
                          color: theme.colors.success.main,
                          ml: 0.5,
                        }} 
                      />
                    )}
                  </Box>
                }
                component={NavLink}
                to={tab.path}
              />
            );
          })}
        </Tabs>
      </Box>

      <Box>
        <Outlet />
      </Box>
    </Box>
  );
};
